import { ANALYST_ACTION_FALLBACKS } from "@/constants/messages";
import { userFacingErrorMessage } from "@/utils/extract-rtk-error";
import { ConciliationEmailReportType } from '@/pages/conciliation/email-report/components/conciliation-email-report-types';
import { useGetEmailReportQuery, useResendEmailMutation, useReviewEmailItemsIndividualMutation } from '@/services/conciliation/email-report';
import { Button } from 'components/ui/button';
import { useFilterState } from '../../ducks/filter/hooks/filterState';
import { GroupedEmailTable } from "./components/email-table";
import { useColumns } from "./columns";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { EmailData } from "./types";
import { toast } from "components/ui/use-toast";
import { EmailItemsReview } from "./components/email-items-review-main";
import { useSteps } from "./hooks/use-steps";
import { IoRefresh } from "react-icons/io5";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface EmailReviewProps {
    conciliation_id?: string;
}

const EmailReview = ({ conciliation_id }: EmailReviewProps) => {
    const filterState = useFilterState();
    const { handleModal } = useDialogModal();
    const { currentStep, selectedEmail, showTable, showReview } = useSteps();
    const [reviewEmailItemsIndividual] = useReviewEmailItemsIndividualMutation();
    const [resendEmail] = useResendEmailMutation();

    const { filters } = filterState || { filters: {} };

    const { data: emails, refetch, isFetching } = useGetEmailReportQuery({
        search: filters.search,
        reportType: filters.reportType ? filters.reportType : ConciliationEmailReportType.EMAILS_BY_CONCILIATION,
        ...(filters.storeCode ? { storeCode: filters.storeCode } : {}),
        ...(filters.regional ? { regional: filters.regional } : {}),
        ...(filters.operator ? { operator: filters.operator } : {}),
        ...(filters.date ? { date: filters.date } : {}),
        ...(filters.sent_at ? { sent_at: filters.sent_at } : {}),
        ...(filters.storeAllowedToReceiveEmails ? { storeAllowedToReceiveEmails: filters.storeAllowedToReceiveEmails } : {}),
        ...(filters.type ? { type: filters.type } : {}),
        ...(filters.is_finalized ? { is_finalized: filters.is_finalized } : {}),
        ...(filters.from_date ? { from_date: filters.from_date } : {}),
        ...(filters.to_date ? { to_date: filters.to_date } : {}),
        conciliation_id: conciliation_id as string,
        page: 1,
        meta: {
            page: 1,
        },
    });

    const handleRefetch = () => {
        refetch();
    };

    const isRefetching = isFetching;

    const columns = useColumns({
        onShowReview: showReview,
    });

    const emailData: EmailData[] = emails?.data || [];

    // selectedEmail é um snapshot fixado no clique de "Revisar Evidências"
    // — não atualiza sozinho após refetch. Para a UI refletir mudanças
    // (ex.: timestamps por review após reenvio parcial), reatamos o objeto
    // pelo email_id à versão mais recente do array refetch'ado.
    const refreshedSelectedEmail = useMemo(() => {
        if (!selectedEmail) return null;
        return (
            emailData.find((e) => e.email_id === selectedEmail.email_id) ??
            selectedEmail
        );
    }, [emailData, selectedEmail]);

    const handleReviewItems = async (items: any[]) => {
        if (!selectedEmail) return;

        try {
            const response = await reviewEmailItemsIndividual({
                conciliation_email_id: selectedEmail.email_id,
                items: items,
            }).unwrap();

            // Verificar se houve erros na revisão
            if (response.errors && response.errors.length > 0) {
                const errorCount = response.errors.length;
                const successCount = response.successful.length;

                toast({
                    variant: "destructive",
                    title: "Revisão parcialmente concluída",
                    description: `${successCount} item(s) revisados, ${errorCount} falharam.`,
                    duration: 5000,
                });
            } else {
                toast({
                    title: "Sucesso!",
                    description: `${response.successful.length} item(s) revisados com sucesso!`,
                    duration: 3000,
                });
            }

            refetch();
            showTable();
        } catch (error) {
            toast({
                variant: "destructive",
                description: userFacingErrorMessage(
                    error,
                    ANALYST_ACTION_FALLBACKS.REVIEW_ITEMS_FAILED
                ),
                duration: 3000,
            });
        }
    };

    const handleResendEmail = async (solicitationId: number) => {
        try {
            const response = await resendEmail({
                solicitation_id: solicitationId,
            }).unwrap();

            toast({
                title: "Email reenviado!",
                description: response.message,
                duration: 3000,
            });

            refetch();
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Erro ao reenviar",
                description: userFacingErrorMessage(
                    error,
                    ANALYST_ACTION_FALLBACKS.RESEND_EMAIL_FAILED
                ),
                duration: 5000,
            });
            throw error;
        }
    };

    return (
        <div className='min-w-[80vw] w-[80vw]'>
            {currentStep === 'table' && (
                <>
                    <GroupedEmailTable
                        data={emailData}
                        columns={columns}
                    />

                    <div className="flex items-center justify-between gap-3 mt-4 p-3 bg-[#F9FBFC] rounded-lg border">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                type="button"
                                onClick={() =>
                                    handleModal({
                                        isOpen: false,
                                    })
                                }
                            >
                                Voltar
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleRefetch}
                                className="hover:text-white text-[#4B303C]"
                                disabled={isRefetching}
                            >
                                <IoRefresh className={cn("w-4 h-4 transition-transform duration-500", isRefetching && "animate-spin")} />
                            </Button>
                        </div>
                    </div>
                </>
            )}

            {currentStep === 'review' && refreshedSelectedEmail && (
                <EmailItemsReview
                    email={refreshedSelectedEmail}
                    onBack={showTable}
                    onApproveItems={handleReviewItems}
                    onResendEmail={handleResendEmail}
                />
            )}
        </div>
    )
}
export default EmailReview
